import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'uinix-ui';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Nullable as EdsNullable } from '~/eds/src/types/Nullable';
import { Filter } from '~/evifields/src/types/Filter';
import { FilterViewType } from '~/evifields/src/types/FilterView';
import { AsyncValue, EnumValue } from '~/evifields/src/types/Value';
import { useMaxHeightBasedOnDistance } from '~/hooks/useMaxHeightBasedOnDistance';
import { useTrackSegment } from '~/hooks/useTrackSegment ';
import { Nullable } from '~/types/Nullable';

import { AsyncSelectComponent } from '../AsyncSelectComponent';
import { TextDelimitedEnumSetValue } from '../subcomponents/TextDelimitedEnumSetValue_DEPRECATED';
import { Field } from '../types';
import { testEnableSearch } from '../utils';

interface Props {
  field: Field<'enum_set'>;
  filter: Filter<EnumValue>;
  onChange: (
    updatedValues: EnumValue[],
    updatedAsyncValue?: AsyncValue<EnumValue>,
  ) => void;
  onViewChange?: (filterView: EdsNullable<FilterViewType>) => void;
}

export const EnumSetValues = ({
  field,
  filter,
  onChange,
  onViewChange,
}: Props) => {
  const intl = useIntl();
  const theme = useTheme();
  const { render, settings, id: fieldId } = field;
  const { options, __testEnableSelectAll } = settings;
  const { asyncValue, id, values, filterView } = filter;

  const [isMultiselect, _toggle, showMultiselect, showCopyPaste] = useToggle(
    !onViewChange || !filterView || filterView !== 'text-delimited',
  );
  const [selectAllCount, setSelectAllCount] = useState<string | undefined>();

  const maxHUpperLimit = theme.sizes['select.menu.max-height'] as number;
  const [containerRef, maxH] = useMaxHeightBasedOnDistance<HTMLDivElement>({
    currentMaxHeight: maxHUpperLimit,
  });

  const isAsync = Boolean(render);
  const enableSearch = testEnableSearch(isAsync, options?.length || 0);
  const enableSelectAll = __testEnableSelectAll?.(filter);
  const trackSegment = useTrackSegment();
  const handleChange = (
    updatedValues: Nullable<EnumValue[]>,
    updatedAsyncValue?: AsyncValue<EnumValue>,
  ) => {
    if (updatedAsyncValue) {
      if (updatedAsyncValue.isAllSelected) {
        updatedAsyncValue.selectAllLabel = selectAllCount;
      }
      updatedAsyncValue.isAllSelected = !!(
        enableSelectAll && updatedAsyncValue.isAllSelected
      );
    }
    onChange(updatedValues ?? [], updatedAsyncValue);
  };

  const shouldHaveTextDelimitedView =
    onViewChange && !(fieldId === 'folder' || fieldId === 'document_group_id');

  const onCopyPasteClick = () => {
    onViewChange?.('text-delimited');
    showCopyPaste();
    trackSegment('textDelimitedFilterClicked', { fieldId });
  };

  const onBackClick = () => {
    onViewChange?.(null);
    showMultiselect();
  };

  const innerProps = {
    asyncValue,
    enableControl: enableSearch,
    enableSearchIcon: true,
    enableSelectAll,
    enableSorting: enableSearch,
    isClearable: false,
    isEmbedded: true,
    isMulti: true,
    name: id,
    value: values,
    onChange: handleChange,
    onChangeSelectAll: (value?: string) => {
      setSelectAllCount(value);
    },
    footerAction: shouldHaveTextDelimitedView
      ? {
          level: 'action' as const,
          text: intl.formatMessage({
            id: 'Filters.EnumSetValues_DEPRECATED.pasteValues',
            defaultMessage: 'Paste values',
            description:
              'In a filter dropdown, switches the user to the text input view where they can paste values into a text area',
          }),
          onClick: onCopyPasteClick,
        }
      : undefined,
    menuMaxHeight: maxH,
  };

  const MultiSelect = (
    <div ref={containerRef}>
      <Layout mt={enableSearch ? undefined : -4} mx={-4}>
        {render?.({
          Component: AsyncSelectComponent,
          innerProps,
        }) || <Select {...innerProps} isMulti options={options} width="100%" />}
      </Layout>
    </div>
  );

  const content = isMultiselect ? (
    MultiSelect
  ) : (
    <TextDelimitedEnumSetValue
      fieldId={fieldId}
      values={values}
      onChange={onChange}
      onBackClick={onBackClick}
    />
  );

  return content;
};
